import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { requireStockUnlock, refreshPinStatus } from "@/lib/stockLock";
import { formatIDR, formatDateID, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import CurrencyInput from "@/components/CurrencyInput";
import ProductPicker from "@/components/ProductPicker";
import html2pdf from "html2pdf.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Trash2,
  Search,
  FileText,
  Printer,
  FileDown,
  Eye,
  Pencil,
  Ban,
  X as CloseIcon,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const emptyItem = () => ({ product_id: "", qty_gudang: 0, qty_toko: 0, price: 0 });

// Small drag-handle rendered on the right edge of a TableHead to let user
// resize the column width. Placed absolutely inside a `relative` parent.
const ResizeHandle = ({ onMouseDown }) => (
  <span
    role="separator"
    aria-orientation="vertical"
    onMouseDown={onMouseDown}
    onClick={(e) => e.stopPropagation()}
    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none opacity-0 hover:opacity-100 bg-zinc-300 transition-opacity"
    style={{ userSelect: "none" }}
  />
);

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerProfiles, setCustomerProfiles] = useState([]);
  const [salesProfiles, setSalesProfiles] = useState([]);
  const [company, setCompany] = useState({ name: "", address: "", address2: "", phone: "", email: "", website: "", logo: "", bank_name: "", bank_account_number: "", bank_account_holder: "" });
  const [q, setQ] = useState("");
  const [filterSales, setFilterSales] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  // Default sort: newest first (by date, then by invoice number so identical
  // dates fall back to the natural number order). Persisted so refresh keeps
  // the user's chosen ordering.
  const INVOICE_SORT_KEY = "invoices_sort_by_v1";
  const [sortBy, setSortBy] = useState(() => {
    try {
      return localStorage.getItem(INVOICE_SORT_KEY) || "date-desc";
    } catch {
      return "date-desc";
    }
  });
  useEffect(() => {
    try { localStorage.setItem(INVOICE_SORT_KEY, sortBy); } catch { /* ignore */ }
  }, [sortBy]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null); // invoice object being edited (metadata only)
  const [salesEditId, setSalesEditId] = useState(null); // invoice id whose Sales name is being inline-edited
  const [salesEditValue, setSalesEditValue] = useState("");
  const [customerEditId, setCustomerEditId] = useState(null);
  const [customerEditValue, setCustomerEditValue] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const exportInvoicesXlsx = async () => {
    const ids = selectedIds.size > 0 ? [...selectedIds] : filtered.map((i) => i.id);
    if (ids.length === 0) { toast.error("Tidak ada faktur untuk diekspor"); return; }
    setExportingXlsx(true);
    try {
      const r = await api.post("/invoices/export-xlsx", { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `faktur_penjualan_${todayISO()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${ids.length} faktur diekspor`);
    } catch {
      toast.error("Gagal export faktur");
    } finally {
      setExportingXlsx(false);
    }
  };

  // Resizable column widths for the invoices list table.
  // Persisted in localStorage so user's preferences survive reloads.
  const INVOICE_COL_STORAGE_KEY = "invoice_col_widths_v1";
  const DEFAULT_COL_WIDTHS = {
    select: 40,
    number: 150,
    date: 110,
    sales: 140,
    po: 110,
    customer: 200,
    product: 240,
    qtyMtp: 90,
    qtyLtc: 90,
    total: 130,
    totalPpn: 140,
    actions: 110,
  };
  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(INVOICE_COL_STORAGE_KEY) || "null");
      return { ...DEFAULT_COL_WIDTHS, ...(saved || {}) };
    } catch {
      return DEFAULT_COL_WIDTHS;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(INVOICE_COL_STORAGE_KEY, JSON.stringify(colWidths));
    } catch {
      /* ignore quota errors */
    }
  }, [colWidths]);

  const startColResize = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidths[key] || 100;
    const onMove = (ev) => {
      const delta = ev.clientX - startX;
      setColWidths((w) => ({ ...w, [key]: Math.max(60, Math.min(600, startWidth + delta)) }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const resetColWidths = () => setColWidths(DEFAULT_COL_WIDTHS);

  const { user } = useAuth();
  const role = user?.role || "kasir";
  const isKasir = role === "kasir";

  const [form, setForm] = useState({
    date: todayISO(),
    customer_name: "",
    customer_address: "",
    sales_name: "",
    po_number: "",
    discount_percent: 0,
    tax_percent: 11,
    note: "",
    items: [emptyItem()],
  });

  const load = async () => {
    const [invRes, prodRes, setRes, custRes, salesRes] = await Promise.all([
      api.get("/invoices"),
      api.get("/products"),
      api.get("/settings").catch(() => ({ data: {} })),
      api.get("/customers").catch(() => ({ data: [] })),
      api.get("/sales").catch(() => ({ data: [] })),
    ]);
    setItems(invRes.data);
    setProducts(prodRes.data);
    setCustomerProfiles(custRes.data || []);
    setSalesProfiles(salesRes.data || []);
    setCompany({
      name: setRes.data?.name || "",
      address: setRes.data?.address || "",
      address2: setRes.data?.address2 || "",
      phone: setRes.data?.phone || "",
      email: setRes.data?.email || "",
      website: setRes.data?.website || "",
      logo: setRes.data?.logo || "",
      bank_name: setRes.data?.bank_name || "",
      bank_account_number: setRes.data?.bank_account_number || "",
      bank_account_holder: setRes.data?.bank_account_holder || "",
    });
  };

  useEffect(() => {
    load();
  }, []);

  // If the Products page seeded a "quick invoice" payload in sessionStorage,
  // pre-fill the new-invoice form with those product lines and open the dialog.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("quick_invoice_prefill_v1");
      if (!raw) return;
      sessionStorage.removeItem("quick_invoice_prefill_v1");
      const parsed = JSON.parse(raw);
      const picks = Array.isArray(parsed?.items) ? parsed.items : [];
      if (picks.length === 0) return;
      const seeded = picks.map((p) => ({
        product_id: p.product_id,
        qty_gudang: 0,
        qty_toko: 0,
        price: Number(p.price) || 0,
      }));
      setForm((f) => ({ ...f, items: seeded }));
      setOpen(true);
      toast.success(`${seeded.length} barang siap difakturkan`);
    } catch {
      /* ignore malformed payloads */
    }
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = items.filter((i) => {
      if (s) {
        const matchesQ =
          (i.number || "").toLowerCase().includes(s) ||
          (i.customer_name || "").toLowerCase().includes(s) ||
          (i.items || []).some((it) =>
            (it.product_name || "").toLowerCase().includes(s) ||
            (it.product_code || "").toLowerCase().includes(s)
          );
        if (!matchesQ) return false;
      }
      if (filterSales !== "all" && (i.sales_name || "").trim() !== filterSales) return false;
      if (filterMonth !== "all") {
        const ym = (i.date || "").slice(0, 7); // YYYY-MM
        if (ym !== filterMonth) return false;
      }
      return true;
    });
    const sorted = [...list];
    const cmpNum = (a, b) =>
      (a.number || "").localeCompare(b.number || "", "id", { numeric: true });
    switch (sortBy) {
      case "date-asc":
        sorted.sort((a, b) =>
          (a.date || "").localeCompare(b.date || "") || cmpNum(a, b),
        );
        break;
      case "number-asc":
        sorted.sort(cmpNum);
        break;
      case "number-desc":
        sorted.sort((a, b) => cmpNum(b, a));
        break;
      case "total-desc":
        sorted.sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0));
        break;
      case "total-asc":
        sorted.sort((a, b) => (Number(a.total) || 0) - (Number(b.total) || 0));
        break;
      case "customer-asc":
        sorted.sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || "", "id"));
        break;
      case "date-desc":
      default:
        sorted.sort((a, b) =>
          (b.date || "").localeCompare(a.date || "") || cmpNum(b, a),
        );
        break;
    }
    return sorted;
  }, [items, q, filterSales, filterMonth, sortBy]);

  // Unique YYYY-MM buckets from invoice dates, sorted newest first.
  const monthOptions = useMemo(() => {
    const set = new Set();
    items.forEach((inv) => {
      const ym = (inv.date || "").slice(0, 7);
      if (ym) set.add(ym);
    });
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [items]);

  const MONTH_ID = [
    "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
  ];
  const formatMonthLabel = (ym) => {
    const [y, m] = ym.split("-");
    const idx = Math.max(0, Math.min(11, Number(m) - 1));
    return `${MONTH_ID[idx]} ${y}`;
  };

  // Book of known customers — merged from customer profiles (with phone/email/npwp)
  // and any invoice history. Keyed by name (case-insensitive).
  const customerBook = useMemo(() => {
    const map = new Map();
    // Start with saved profiles (richest data)
    for (const c of customerProfiles) {
      const name = (c.name || "").trim();
      if (!name) continue;
      map.set(name.toLowerCase(), {
        name,
        address: c.address || "",
        phone: c.phone || "",
        email: c.email || "",
        npwp: c.npwp || "",
        city: c.city || "",
        note: c.note || "",
      });
    }
    // Backfill from invoices for customers without a saved profile
    const sorted = [...items].sort((a, b) => (a.date < b.date ? 1 : -1));
    for (const inv of sorted) {
      const name = (inv.customer_name || "").trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          name,
          address: (inv.customer_address || "").trim(),
          phone: "",
          email: "",
          npwp: "",
          city: "",
          note: "",
        });
      }
    }
    return map;
  }, [items, customerProfiles]);

  const customerOptions = useMemo(
    () => Array.from(customerBook.values()).sort((a, b) => a.name.localeCompare(b.name, "id")),
    [customerBook],
  );

  // Sales book — merged from sales profiles (with phone/email) and invoice history.
  const salesBook = useMemo(() => {
    const map = new Map();
    for (const s of salesProfiles) {
      const name = (s.name || "").trim();
      if (!name) continue;
      map.set(name.toLowerCase(), {
        name,
        phone: s.phone || "",
        email: s.email || "",
        address: s.address || "",
        note: s.note || "",
      });
    }
    items.forEach((inv) => {
      const s = (inv.sales_name || "").trim();
      if (!s) return;
      const key = s.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { name: s, phone: "", email: "", address: "", note: "" });
      }
    });
    return map;
  }, [salesProfiles, items]);

  const salesOptions = useMemo(
    () => Array.from(salesBook.values()).map((s) => s.name).sort((a, b) => a.localeCompare(b, "id")),
    [salesBook],
  );

  // Detect duplicate PO number (case-insensitive, trimmed) so we can warn user.
  const poDuplicateNew = useMemo(() => {
    const target = (form.po_number || "").trim().toLowerCase();
    if (!target) return null;
    const match = items.find((inv) => (inv.po_number || "").trim().toLowerCase() === target);
    return match || null;
  }, [items, form.po_number]);

  const poDuplicateEdit = useMemo(() => {
    if (!editing) return null;
    const target = (editing.po_number || "").trim().toLowerCase();
    if (!target) return null;
    const match = items.find(
      (inv) => inv.id !== editing.id && (inv.po_number || "").trim().toLowerCase() === target,
    );
    return match || null;
  }, [items, editing]);

  const subtotal = useMemo(
    () =>
      form.items.reduce(
        (s, it) =>
          s +
          ((Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0)) *
            (Number(it.price) || 0),
        0
      ),
    [form.items]
  );
  const discountAmount = subtotal * ((Number(form.discount_percent) || 0) / 100);
  const afterDiscount = Math.max(0, subtotal - discountAmount);
  const taxAmount = afterDiscount * ((Number(form.tax_percent) || 0) / 100);
  const grandTotal = afterDiscount + taxAmount;

  const setItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeRow = (idx) =>
    setForm((f) => ({
      ...f,
      items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items,
    }));

  const onPickProduct = (idx, product_id) => {
    const p = products.find((x) => x.id === product_id);
    setItem(idx, { product_id, price: p?.sell_price || 0 });
  };

  const resetForm = () => {
    setForm({
      date: todayISO(),
      customer_name: "",
      customer_address: "",
      sales_name: "",
      po_number: "",
      discount_percent: 0,
      tax_percent: 11,
      note: "",
      items: [emptyItem()],
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      toast.error("Nama pelanggan wajib diisi");
      return;
    }
    const cleanItems = form.items.filter(
      (it) =>
        it.product_id &&
        (Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0) > 0
    );
    if (cleanItems.length === 0) {
      toast.error("Tambahkan minimal 1 item ke faktur");
      return;
    }
    try {
      const r = await api.post("/invoices", {
        date: form.date,
        customer_name: form.customer_name,
        customer_address: form.customer_address,
        sales_name: form.sales_name,
        po_number: form.po_number,
        discount: Math.round(discountAmount),
        discount_percent: Number(form.discount_percent) || 0,
        tax_percent: Number(form.tax_percent) || 0,
        note: form.note,
        items: cleanItems.map((it) => ({
          product_id: it.product_id,
          qty_gudang: Number(it.qty_gudang) || 0,
          qty_toko: Number(it.qty_toko) || 0,
          price: Number(it.price) || 0,
        })),
      });
      toast.success(`Faktur ${r.data.number} tersimpan`);
      setOpen(false);
      resetForm();
      load();
      setViewing(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan faktur");
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Hapus faktur ${inv.number}? Stok akan dikembalikan.`)) return;
    if (!(await requireStockUnlock())) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Faktur dihapus");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const cancelInvoice = async (inv) => {
    if (inv.canceled) {
      toast.info("Faktur ini sudah dibatalkan.");
      return;
    }
    if (!window.confirm(
      `Batalkan faktur ${inv.number}?\n\n` +
      `Stok akan dikembalikan dan faktur akan ditandai DIBATALKAN.\n` +
      `Nama barang akan disensor. Data faktur tetap tersimpan.`
    )) return;
    try {
      await api.post(`/invoices/${inv.id}/cancel`);
      toast.success(`Faktur ${inv.number} dibatalkan`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membatalkan faktur");
    }
  };

  const beginSalesEdit = (inv) => {
    setSalesEditId(inv.id);
    setSalesEditValue(inv.sales_name || "");
  };
  const cancelSalesEdit = () => {
    setSalesEditId(null);
    setSalesEditValue("");
  };
  const saveSalesEdit = async (inv) => {
    const newName = salesEditValue.trim();
    if (!newName) {
      toast.error("Nama sales wajib diisi");
      return;
    }
    if (newName === (inv.sales_name || "").trim()) {
      cancelSalesEdit();
      return;
    }
    try {
      await api.patch(`/invoices/${inv.id}`, { sales_name: newName });
      toast.success("Nama sales diperbarui");
      cancelSalesEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui sales");
    }
  };

  const beginCustomerEdit = (inv) => {
    setCustomerEditId(inv.id);
    setCustomerEditValue(inv.customer_name || "");
  };
  const cancelCustomerEdit = () => {
    setCustomerEditId(null);
    setCustomerEditValue("");
  };
  const saveCustomerEdit = async (inv) => {
    const newName = customerEditValue.trim();
    if (!newName) {
      toast.error("Nama pelanggan wajib diisi");
      return;
    }
    if (newName === (inv.customer_name || "").trim()) {
      cancelCustomerEdit();
      return;
    }
    try {
      await api.patch(`/invoices/${inv.id}`, { customer_name: newName });
      toast.success("Nama pelanggan diperbarui");
      cancelCustomerEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui pelanggan");
    }
  };

  const openEdit = (inv) => {
    const invSubtotal = (inv.items || []).reduce(
      (s, it) => s + ((Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0)) * (Number(it.price) || 0),
      0,
    );
    const storedDiscount = Number(inv.discount) || 0;
    // Prefer explicit discount_percent if backend returned one, otherwise derive from amount.
    const derivedPct = invSubtotal > 0 ? (storedDiscount / invSubtotal) * 100 : 0;
    const discountPercent = inv.discount_percent != null
      ? Number(inv.discount_percent) || 0
      : Math.round(derivedPct * 100) / 100;
    setEditing({
      id: inv.id,
      number: inv.number,
      date: inv.date || todayISO(),
      customer_name: inv.customer_name || "",
      customer_address: inv.customer_address || "",
      sales_name: inv.sales_name || "",
      po_number: inv.po_number || "",
      note: inv.note || "",
      discount_percent: discountPercent,
      tax_percent: Number(inv.tax_percent) || 0,
      items: (inv.items || []).map((it) => ({
        product_id: it.product_id,
        qty_gudang: Number(it.qty_gudang) || 0,
        qty_toko: Number(it.qty_toko) || 0,
        price: Number(it.price) || 0,
      })),
    });
  };

  const setEditItem = (idx, patch) => {
    setEditing((e) => ({
      ...e,
      items: e.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };
  const addEditRow = () =>
    setEditing((e) => ({ ...e, items: [...e.items, emptyItem()] }));
  const removeEditRow = (idx) =>
    setEditing((e) => ({
      ...e,
      items: e.items.length > 1 ? e.items.filter((_, i) => i !== idx) : e.items,
    }));
  const onPickEditProduct = (idx, product_id) => {
    const p = products.find((x) => x.id === product_id);
    setEditItem(idx, { product_id, price: p?.sell_price || 0 });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    if (!editing.customer_name.trim()) {
      toast.error("Nama pelanggan wajib diisi");
      return;
    }
    if (!editing.sales_name.trim()) {
      toast.error("Nama sales wajib diisi");
      return;
    }
    const cleanItems = editing.items.filter(
      (it) =>
        it.product_id &&
        (Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0) > 0
    );
    if (cleanItems.length === 0) {
      toast.error("Faktur harus punya minimal 1 item");
      return;
    }
    if (!(await requireStockUnlock())) return;
    try {
      const editSubtotalForSave = editing.items.reduce(
        (s, it) => s + ((Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0)) * (Number(it.price) || 0),
        0,
      );
      const editDiscAmt = editSubtotalForSave * ((Number(editing.discount_percent) || 0) / 100);
      await api.patch(`/invoices/${editing.id}`, {
        date: editing.date,
        customer_name: editing.customer_name,
        customer_address: editing.customer_address,
        sales_name: editing.sales_name,
        po_number: editing.po_number,
        note: editing.note,
        discount: Math.round(editDiscAmt),
        discount_percent: Number(editing.discount_percent) || 0,
        tax_percent: Number(editing.tax_percent) || 0,
        items: cleanItems.map((it) => ({
          product_id: it.product_id,
          qty_gudang: Number(it.qty_gudang) || 0,
          qty_toko: Number(it.qty_toko) || 0,
          price: Number(it.price) || 0,
        })),
      });
      toast.success(`Faktur ${editing.number} diperbarui`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memperbarui faktur");
    }
  };

  // ---- Bulk selection & delete ----------------------------------------
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allFilteredIds = useMemo(() => filtered.map((i) => i.id), [filtered]);
  const allSelectedInView =
    allFilteredIds.length > 0 && allFilteredIds.every((id) => selectedIds.has(id));
  const toggleAllInView = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedInView) allFilteredIds.forEach((id) => next.delete(id));
      else allFilteredIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Hapus ${selectedIds.size} faktur terpilih? Stok masing-masing akan dikembalikan.`)) return;
    if (!(await requireStockUnlock())) return;
    try {
      await Promise.all(Array.from(selectedIds).map((id) => api.delete(`/invoices/${id}`)));
      toast.success(`${selectedIds.size} faktur dihapus`);
      clearSelection();
      load();
    } catch (err) {
      toast.error("Sebagian gagal dihapus");
      load();
    }
  };

  const printInvoice = () => window.print();

  const [savingPdf, setSavingPdf] = useState(false);
  const savePdfInvoice = async () => {
    const el = document.querySelector(".print-area");
    if (!el) {
      toast.error("Konten faktur tidak ditemukan");
      return;
    }
    setSavingPdf(true);
    try {
      // Sanitise customer name for use in filename (remove characters that
      // browsers/OS may reject and collapse whitespace).
      const rawCustomer = (viewing?.customer_name || "").trim();
      const safeCustomer = rawCustomer
        .replace(/[\\/:*?"<>|]/g, "")
        .replace(/\s+/g, "-");
      const parts = [viewing?.number || "invoice", safeCustomer].filter(Boolean);
      const filename = `Faktur-${parts.join("-")}.pdf`;
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(el)
        .save();
      toast.success("PDF berhasil disimpan");
    } catch (err) {
      toast.error("Gagal membuat PDF");
    } finally {
      setSavingPdf(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-end justify-between flex-wrap gap-3 no-print">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-zinc-900 text-white">
              <FileText className="h-4 w-4" />
            </span>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Faktur Penjualan</h1>
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            Buat faktur untuk pelanggan; stok toko otomatis berkurang.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          data-testid="btn-export-invoices-xlsx"
          variant="outline"
          onClick={exportInvoicesXlsx}
          disabled={exportingXlsx}
          className="rounded-md"
        >
          <FileDown className="h-4 w-4 mr-1.5" />
          {exportingXlsx ? "Mengekspor..." : selectedIds.size > 0 ? `Export Excel (${selectedIds.size})` : "Export Excel"}
        </Button>
        <Button
          data-testid="btn-add-invoice"
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
          className="bg-zinc-900 hover:bg-zinc-800 rounded-md"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Buat Faktur
        </Button>
        </div>
      </div>

      <Card className="border-zinc-200 shadow-none rounded-md no-print">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="invoices-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nomor faktur, pelanggan, atau nama barang…"
              className="pl-9 h-10 rounded-md"
            />
          </div>
          <Select value={filterSales} onValueChange={setFilterSales}>
            <SelectTrigger className="h-10 w-[180px]" data-testid="invoices-filter-sales">
              <SelectValue placeholder="Sales" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Sales</SelectItem>
              {salesOptions.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="h-10 w-[180px]" data-testid="invoices-filter-month">
              <SelectValue placeholder="Bulan" />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="all">Semua Bulan</SelectItem>
              {monthOptions.map((ym) => (
                <SelectItem key={ym} value={ym}>{formatMonthLabel(ym)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10 w-[210px]" data-testid="invoices-sort-by">
              <SelectValue placeholder="Urutkan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Tanggal Terbaru</SelectItem>
              <SelectItem value="date-asc">Tanggal Terlama</SelectItem>
              <SelectItem value="number-desc">No. Faktur (Besar → Kecil)</SelectItem>
              <SelectItem value="number-asc">No. Faktur (Kecil → Besar)</SelectItem>
              <SelectItem value="total-desc">Total Tertinggi</SelectItem>
              <SelectItem value="total-asc">Total Terendah</SelectItem>
              <SelectItem value="customer-asc">Pelanggan (A → Z)</SelectItem>
            </SelectContent>
          </Select>
          {(filterSales !== "all" || filterMonth !== "all" || q || sortBy !== "date-desc") && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setQ("");
                setFilterSales("all");
                setFilterMonth("all");
                setSortBy("date-desc");
              }}
              className="h-9 text-xs"
              data-testid="invoices-filter-reset"
            >
              Reset
            </Button>
          )}
          <div className="text-xs text-zinc-500">
            {filtered.length} dari {items.length} faktur
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div
            className="px-4 py-3 border-b border-zinc-200 bg-zinc-900 text-white flex items-center gap-3 flex-wrap"
            data-testid="invoices-bulk-actions-bar"
          >
            <div className="text-sm font-medium">{selectedIds.size} faktur dipilih</div>
            <div className="flex-1" />
            <Button
              data-testid="btn-invoices-bulk-delete"
              size="sm"
              variant="outline"
              onClick={bulkDelete}
              className="h-8 rounded-md bg-rose-600 text-white border-rose-600 hover:bg-rose-700 hover:text-white"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Hapus Terpilih
            </Button>
            <Button
              data-testid="btn-invoices-bulk-clear"
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="h-8 rounded-md text-white hover:bg-white/10 hover:text-white"
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
              Batal
            </Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: colWidths.select }} />
              <col style={{ width: colWidths.number }} />
              <col style={{ width: colWidths.date }} />
              <col style={{ width: colWidths.sales }} />
              <col style={{ width: colWidths.po }} />
              <col style={{ width: colWidths.customer }} />
              <col style={{ width: colWidths.product }} />
              <col style={{ width: colWidths.qtyMtp }} />
              <col style={{ width: colWidths.qtyLtc }} />
              <col style={{ width: colWidths.total }} />
              <col style={{ width: colWidths.totalPpn }} />
              <col style={{ width: colWidths.actions }} />
            </colgroup>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                <TableHead className="relative">
                  <Checkbox
                    data-testid="invoices-bulk-select-all"
                    checked={allSelectedInView}
                    onCheckedChange={toggleAllInView}
                    aria-label="Pilih semua faktur"
                  />
                  <ResizeHandle onMouseDown={startColResize("select")} />
                </TableHead>
                <TableHead className="relative">
                  No. Faktur
                  <ResizeHandle onMouseDown={startColResize("number")} />
                </TableHead>
                <TableHead className="relative">
                  Tanggal
                  <ResizeHandle onMouseDown={startColResize("date")} />
                </TableHead>
                <TableHead className="relative hidden lg:table-cell">
                  Sales
                  <ResizeHandle onMouseDown={startColResize("sales")} />
                </TableHead>
                <TableHead className="relative hidden lg:table-cell">
                  No. PO
                  <ResizeHandle onMouseDown={startColResize("po")} />
                </TableHead>
                <TableHead className="relative">
                  Pelanggan
                  <ResizeHandle onMouseDown={startColResize("customer")} />
                </TableHead>
                <TableHead className="relative hidden md:table-cell">
                  Nama Barang
                  <ResizeHandle onMouseDown={startColResize("product")} />
                </TableHead>
                <TableHead className="text-right hidden sm:table-cell relative">
                  Qty MTP
                  <ResizeHandle onMouseDown={startColResize("qtyMtp")} />
                </TableHead>
                <TableHead className="text-right hidden sm:table-cell relative">
                  Qty LTC
                  <ResizeHandle onMouseDown={startColResize("qtyLtc")} />
                </TableHead>
                <TableHead className="text-right relative">
                  Total
                  <ResizeHandle onMouseDown={startColResize("total")} />
                </TableHead>
                <TableHead className="text-right relative">
                  Total + PPN 11%
                  <ResizeHandle onMouseDown={startColResize("totalPpn")} />
                </TableHead>
                <TableHead className="text-right relative">
                  <button
                    type="button"
                    onClick={resetColWidths}
                    title="Reset ukuran kolom"
                    data-testid="btn-reset-col-widths"
                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-800 hover:underline"
                  >
                    Aksi
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="invoices-table-body">
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-10 text-zinc-500">
                    <FileText className="h-6 w-6 mx-auto mb-2 opacity-40" />
                    Belum ada faktur. Klik "Buat Faktur" untuk mulai.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((inv) => {
                const isSelected = selectedIds.has(inv.id);
                return (
                  <TableRow
                    key={inv.id}
                    data-state={isSelected ? "selected" : undefined}
                    className={`transition-colors duration-150 ${
                      isSelected ? "bg-zinc-100 hover:bg-zinc-100" : "hover:bg-zinc-50"
                    }`}
                  >
                    <TableCell>
                      <Checkbox
                        data-testid={`invoices-bulk-select-${inv.number}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleOne(inv.id)}
                        aria-label={`Pilih ${inv.number}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-zinc-800">
                      <div className="flex items-center gap-1.5">
                        <span className={inv.canceled ? "line-through text-zinc-400" : ""}>{inv.number}</span>
                        {inv.canceled && (
                          <span
                            data-testid={`invoice-canceled-badge-${inv.number}`}
                            className="text-[9px] uppercase tracking-widest font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5"
                          >
                            Dibatalkan
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-600 whitespace-nowrap">{formatDateID(inv.date)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-zinc-600 text-sm">
                      {salesEditId === inv.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            autoFocus
                            data-testid={`quick-sales-input-${inv.number}`}
                            list={`quick-sales-datalist-${inv.number}`}
                            value={salesEditValue}
                            onChange={(e) => setSalesEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveSalesEdit(inv);
                              if (e.key === "Escape") cancelSalesEdit();
                            }}
                            onBlur={() => saveSalesEdit(inv)}
                            className="h-7 text-xs px-1"
                          />
                          <datalist id={`quick-sales-datalist-${inv.number}`}>
                            {salesOptions.map((s) => (
                              <option key={s} value={s} />
                            ))}
                          </datalist>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            beginSalesEdit(inv);
                          }}
                          data-testid={`quick-sales-btn-${inv.number}`}
                          title="Klik untuk ubah nama sales"
                          className="group inline-flex items-center gap-1 text-left rounded px-1 -mx-1 py-0.5 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                        >
                          {inv.sales_name || <span className="text-zinc-300">—</span>}
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 text-zinc-500 shrink-0" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell font-mono text-xs text-zinc-600" data-testid={`invoice-po-${inv.number}`}>
                      {inv.po_number || <span className="text-zinc-300">—</span>}
                    </TableCell>
                    <TableCell className="font-medium">
                      {customerEditId === inv.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            autoFocus
                            data-testid={`quick-customer-input-${inv.number}`}
                            list={`quick-customer-datalist-${inv.number}`}
                            value={customerEditValue}
                            onChange={(e) => setCustomerEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveCustomerEdit(inv);
                              if (e.key === "Escape") cancelCustomerEdit();
                            }}
                            onBlur={() => saveCustomerEdit(inv)}
                            className="h-7 text-sm px-1"
                          />
                          <datalist id={`quick-customer-datalist-${inv.number}`}>
                            {customerOptions.map((c) => (
                              <option key={c.name} value={c.name} />
                            ))}
                          </datalist>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            beginCustomerEdit(inv);
                          }}
                          data-testid={`quick-customer-btn-${inv.number}`}
                          title="Klik untuk ubah nama pelanggan"
                          className="group inline-flex items-center gap-1 text-left rounded px-1 -mx-1 py-0.5 hover:bg-zinc-100 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                        >
                          {inv.customer_name || <span className="text-zinc-300">—</span>}
                          <Pencil className="h-3 w-3 opacity-0 group-hover:opacity-60 text-zinc-500 shrink-0" />
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-zinc-700 max-w-[360px]">
                      {(inv.items || []).length > 0 ? (
                        <ul className="space-y-0.5 text-sm">
                          {inv.items.map((it, idx) => {
                            const prod = products.find((p) => p.id === it.product_id);
                            const productPo = prod?.po_number || "";
                            const displayCode = inv.canceled ? "•••" : it.product_code;
                            const displayName = inv.canceled ? "•••••• (dibatalkan)" : it.product_name;
                            return (
                              <li
                                key={idx}
                                className={`leading-tight ${inv.canceled ? "opacity-60" : ""}`}
                                title={inv.canceled ? "Data barang disensor karena faktur dibatalkan" : it.product_name}
                              >
                                <div>
                                  <span className="text-zinc-400 mr-1">•</span>
                                  <span className="font-mono text-zinc-500 mr-1.5">
                                    {displayCode}
                                  </span>
                                  {!inv.canceled && productPo && (
                                    <span
                                      className="font-mono text-zinc-500"
                                      data-testid={`invoice-item-po-${inv.number}-${idx}`}
                                    >
                                      {productPo}
                                    </span>
                                  )}
                                </div>
                                <div className="ml-4 text-zinc-700 truncate">
                                  {displayName}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right num text-zinc-600 hidden sm:table-cell" data-testid={`invoice-qty-mtp-${inv.number}`}>
                      {inv.canceled ? (
                        <span className="text-zinc-400">0</span>
                      ) : (inv.items || []).length > 0 ? (
                        <ul className="space-y-0.5 text-xs">
                          {inv.items.map((it, idx) => (
                            <li key={idx} className="whitespace-nowrap">
                              {Number(it.qty_gudang) || 0}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell className="text-right num text-zinc-600 hidden sm:table-cell" data-testid={`invoice-qty-ltc-${inv.number}`}>
                      {inv.canceled ? (
                        <span className="text-zinc-400">0</span>
                      ) : (inv.items || []).length > 0 ? (
                        <ul className="space-y-0.5 text-xs">
                          {inv.items.map((it, idx) => (
                            <li key={idx} className="whitespace-nowrap">
                              {Number(it.qty_toko) || 0}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell className="text-right num" data-testid={`invoice-subtotal-${inv.number}`}>
                      {inv.canceled ? (
                        <span className="text-zinc-400">{formatIDR(0)}</span>
                      ) : (inv.items || []).length > 0 ? (
                        <ul className="space-y-0.5 text-xs font-normal text-zinc-600">
                          {inv.items.map((it, idx) => (
                            <li key={idx} className="whitespace-nowrap">
                              {formatIDR(it.subtotal)}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        formatIDR(inv.subtotal || 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right num font-semibold" data-testid={`invoice-total-ppn-${inv.number}`}>
                      {inv.canceled ? (
                        <span className="text-zinc-400 font-normal">{formatIDR(0)}</span>
                      ) : (
                        formatIDR(inv.total)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          data-testid={`btn-view-invoice-${inv.number}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => setViewing(inv)}
                          className="h-8 w-8"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          data-testid={`btn-cancel-invoice-${inv.number}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => cancelInvoice(inv)}
                          disabled={inv.canceled}
                          className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
                          title={inv.canceled ? "Faktur sudah dibatalkan" : "Batalkan faktur (stok dikembalikan, nama barang disensor)"}
                        >
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                        {!isKasir && (
                          <Button
                            data-testid={`btn-edit-invoice-${inv.number}`}
                            size="icon"
                            variant="ghost"
                            onClick={() => openEdit(inv)}
                            className="h-8 w-8 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100"
                            title="Ubah data faktur"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {!isKasir && (
                          <Button
                            data-testid={`btn-del-invoice-${inv.number}`}
                            size="icon"
                            variant="ghost"
                            onClick={() => remove(inv)}
                            className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {filtered.length > 0 && (() => {
          const activeInvoices = filtered.filter((i) => !i.canceled);
          const canceledCount = filtered.length - activeInvoices.length;
          const totSubtotal = activeInvoices.reduce((s, i) => s + (Number(i.subtotal) || 0), 0);
          const totTax = activeInvoices.reduce((s, i) => s + (Number(i.tax_amount) || 0), 0);
          const totGrand = activeInvoices.reduce((s, i) => s + (Number(i.total) || 0), 0);
          return (
            <div
              className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-4 text-sm"
              data-testid="invoices-total-footer"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Total Penjualan{" "}
                <span className="text-zinc-700 normal-case tracking-normal">
                  ({activeInvoices.length} faktur
                  {canceledCount > 0 && (
                    <span className="text-rose-600 ml-1">
                      · {canceledCount} dibatalkan
                    </span>
                  )}
                  )
                </span>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Subtotal</div>
                  <div
                    className="num font-medium text-zinc-800"
                    data-testid="invoices-total-subtotal"
                  >
                    {formatIDR(totSubtotal)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wider">PPN</div>
                  <div
                    className="num font-medium text-zinc-800"
                    data-testid="invoices-total-tax"
                  >
                    {formatIDR(totTax)}
                  </div>
                </div>
                <div className="text-right border-l border-zinc-300 pl-6">
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Grand Total</div>
                  <div
                    className="num text-lg font-display font-bold text-zinc-900"
                    data-testid="invoices-total-grand"
                  >
                    {formatIDR(totGrand)}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </Card>

      {/* CREATE dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>Buat Faktur Penjualan</DialogTitle>
            <DialogDescription>Stok toko akan otomatis berkurang setelah disimpan.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tanggal</Label>
                <Input
                  data-testid="input-invoice-date"
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Nama Pelanggan</Label>
                <Input
                  data-testid="input-invoice-customer"
                  required
                  list="invoice-customer-list"
                  placeholder="Ketik atau pilih dari daftar…"
                  value={form.customer_name}
                  onChange={(e) => {
                    const name = e.target.value;
                    const known = customerBook.get(name.trim().toLowerCase());
                    setForm((f) => ({
                      ...f,
                      customer_name: name,
                      // auto-fill address when a saved customer is picked (overwrite empty field only)
                      customer_address:
                        known && (!f.customer_address || f.customer_address === "")
                          ? known.address
                          : f.customer_address,
                    }));
                  }}
                  onBlur={(e) => {
                    const known = customerBook.get(e.target.value.trim().toLowerCase());
                    if (known && !form.customer_address) {
                      setForm((f) => ({ ...f, customer_address: known.address }));
                    }
                  }}
                />
                <datalist id="invoice-customer-list">
                  {customerOptions.map((c) => (
                    <option key={c.name} value={c.name} />
                  ))}
                </datalist>
                {(() => {
                  const known = customerBook.get((form.customer_name || "").trim().toLowerCase());
                  if (known && (known.phone || known.email || known.npwp || known.city)) {
                    return (
                      <div
                        data-testid="invoice-customer-preview"
                        className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-2 py-1 mt-1 flex flex-wrap gap-x-3 gap-y-0.5"
                      >
                        {known.phone && <span>📱 {known.phone}</span>}
                        {known.email && <span>✉️ {known.email}</span>}
                        {known.city && <span>📍 {known.city}</span>}
                        {known.npwp && <span>🏢 PIC: {known.npwp}</span>}
                      </div>
                    );
                  }
                  if (customerOptions.length > 0) {
                    return (
                      <p className="text-[11px] text-zinc-400 mt-1">
                        {customerOptions.length} pelanggan tersimpan — ketik untuk lihat saran.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="col-span-2">
                <Label>Alamat / Kontak (opsional)</Label>
                <Input
                  data-testid="input-invoice-address"
                  placeholder="Alamat atau nomor HP"
                  value={form.customer_address}
                  onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Nama Sales <span className="text-rose-600">*</span></Label>
                <Input
                  data-testid="input-invoice-sales"
                  required
                  list="invoice-sales-list"
                  placeholder="Sales/marketing yang menangani penjualan ini"
                  value={form.sales_name}
                  onChange={(e) => setForm({ ...form, sales_name: e.target.value })}
                />
                <datalist id="invoice-sales-list">
                  {salesOptions.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                {(() => {
                  const known = salesBook.get((form.sales_name || "").trim().toLowerCase());
                  if (known && (known.phone || known.email)) {
                    return (
                      <div
                        data-testid="invoice-sales-preview"
                        className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-2 py-1 mt-1 flex flex-wrap gap-x-3 gap-y-0.5"
                      >
                        {known.phone && <span>📱 {known.phone}</span>}
                        {known.email && <span>✉️ {known.email}</span>}
                      </div>
                    );
                  }
                  if (salesOptions.length > 0) {
                    return (
                      <p className="text-[11px] text-zinc-400 mt-1">
                        {salesOptions.length} nama sales tersimpan — ketik untuk lihat saran.
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="col-span-2">
                <Label>Nomor PO Pembelian (opsional)</Label>
                <Input
                  data-testid="input-invoice-po"
                  placeholder="Nomor Purchase Order dari pelanggan"
                  value={form.po_number}
                  onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                  className={poDuplicateNew ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                />
                {poDuplicateNew && (
                  <p
                    data-testid="po-duplicate-warning-new"
                    className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5"
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-[1px]" />
                    <span>
                      Nomor PO ini sudah dipakai di faktur{" "}
                      <span className="font-semibold">{poDuplicateNew.number}</span>
                      {" "}({poDuplicateNew.customer_name || "-"}). Pastikan tidak duplikat.
                    </span>
                  </p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Item Faktur</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addRow}
                  data-testid="btn-add-invoice-row"
                  className="h-8 rounded-md"
                >
                  <Plus className="h-3 w-3 mr-1" /> Tambah Baris
                </Button>
              </div>
              <div className="border border-zinc-200 rounded-md overflow-x-auto">
                <Table>
                  <TableHeader className="bg-zinc-50">
                    <TableRow>
                      <TableHead>Barang</TableHead>
                      <TableHead className="w-[90px] text-right">Qty MTP</TableHead>
                      <TableHead className="w-[90px] text-right">Qty LTC</TableHead>
                      <TableHead className="w-[160px] text-right">Harga</TableHead>
                      <TableHead className="w-[140px] text-right">Subtotal</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.items.map((it, idx) => {
                      const p = products.find((x) => x.id === it.product_id);
                      const totalQty = (Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0);
                      const rowTotal = totalQty * (Number(it.price) || 0);
                      const overGudang = p && (Number(it.qty_gudang) || 0) > (p.stock_gudang ?? 0);
                      const overToko = p && (Number(it.qty_toko) || 0) > (p.stock_toko ?? 0);
                      return (
                        <TableRow key={idx} className="align-top">
                          <TableCell className="p-2">
                            <ProductPicker
                              testId={`select-invoice-product-${idx}`}
                              products={products}
                              value={it.product_id}
                              onChange={(v) => onPickProduct(idx, v)}
                            />
                            {p && (
                              <div className="text-[10px] text-zinc-500 mt-1">
                                MTP: {p.stock_gudang} · LTC: {p.stock_toko}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              data-testid={`input-invoice-qty-gudang-${idx}`}
                              type="number"
                              min={0}
                              inputMode="numeric"
                              value={it.qty_gudang}
                              onChange={(e) => setItem(idx, { qty_gudang: e.target.value })}
                              className={`text-right h-9 ${overGudang ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              data-testid={`input-invoice-qty-toko-${idx}`}
                              type="number"
                              min={0}
                              inputMode="numeric"
                              value={it.qty_toko}
                              onChange={(e) => setItem(idx, { qty_toko: e.target.value })}
                              className={`text-right h-9 ${overToko ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <CurrencyInput
                              data-testid={`input-invoice-price-${idx}`}
                              value={it.price}
                              onChange={(v) => setItem(idx, { price: v })}
                            />
                          </TableCell>
                          <TableCell className="p-2 text-right num font-medium pt-4">
                            {formatIDR(rowTotal)}
                          </TableCell>
                          <TableCell className="p-2 pt-3">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              onClick={() => removeRow(idx)}
                              disabled={form.items.length === 1}
                              data-testid={`btn-del-invoice-row-${idx}`}
                              className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            >
                              <CloseIcon className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Diskon (%)</Label>
                <Input
                  data-testid="input-invoice-discount"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  max="100"
                  value={form.discount_percent}
                  onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                />
              </div>
              <div>
                <Label>Pajak (%)</Label>
                <Input
                  data-testid="input-invoice-tax"
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={form.tax_percent}
                  onChange={(e) => setForm({ ...form, tax_percent: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Catatan (opsional)</Label>
                <Input
                  data-testid="input-invoice-note"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </div>
            </div>

            <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-600">Subtotal</span>
                <span className="num font-medium">{formatIDR(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Diskon ({Number(form.discount_percent) || 0}%)</span>
                <span className="num text-rose-700">- {formatIDR(discountAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Pajak ({Number(form.tax_percent) || 0}%)</span>
                <span className="num">{formatIDR(taxAmount)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-zinc-200 mt-2">
                <span className="text-zinc-900 font-semibold">Total</span>
                <span
                  className="num text-lg font-display font-bold text-zinc-900"
                  data-testid="invoice-form-total"
                >
                  {formatIDR(grandTotal)}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button data-testid="btn-save-invoice" type="submit" className="bg-zinc-900 hover:bg-zinc-800">
                Simpan Faktur
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* EDIT metadata dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="invoice-edit-dialog">
          <DialogHeader>
            <DialogTitle>Ubah Faktur {editing?.number}</DialogTitle>
            <DialogDescription>
              Ubah data pelanggan, item, dan harga. Stok akan otomatis disesuaikan (stok lama dikembalikan, stok baru dikurangi).
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tanggal</Label>
                  <Input
                    data-testid="edit-invoice-date"
                    type="date"
                    required
                    value={editing.date}
                    onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Nama Pelanggan</Label>
                  <Input
                    data-testid="edit-invoice-customer"
                    required
                    list="edit-invoice-customer-list"
                    value={editing.customer_name}
                    onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })}
                  />
                  <datalist id="edit-invoice-customer-list">
                    {customerOptions.map((c) => (
                      <option key={c.name} value={c.name} />
                    ))}
                  </datalist>
                </div>
                <div className="col-span-2">
                  <Label>Alamat / Kontak</Label>
                  <Input
                    data-testid="edit-invoice-address"
                    value={editing.customer_address}
                    onChange={(e) => setEditing({ ...editing, customer_address: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Nama Sales <span className="text-rose-600">*</span></Label>
                  <Input
                    data-testid="edit-invoice-sales"
                    required
                    list="edit-invoice-sales-list"
                    value={editing.sales_name}
                    onChange={(e) => setEditing({ ...editing, sales_name: e.target.value })}
                  />
                  <datalist id="edit-invoice-sales-list">
                    {salesOptions.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
                <div className="col-span-2">
                  <Label>Nomor PO Pembelian</Label>
                  <Input
                    data-testid="edit-invoice-po"
                    value={editing.po_number}
                    onChange={(e) => setEditing({ ...editing, po_number: e.target.value })}
                    className={poDuplicateEdit ? "border-amber-400 focus-visible:ring-amber-400" : ""}
                  />
                  {poDuplicateEdit && (
                    <p
                      data-testid="po-duplicate-warning-edit"
                      className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 flex items-start gap-1.5"
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-[1px]" />
                      <span>
                        Nomor PO ini sudah dipakai di faktur{" "}
                        <span className="font-semibold">{poDuplicateEdit.number}</span>
                        {" "}({poDuplicateEdit.customer_name || "-"}).
                      </span>
                    </p>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Item Faktur</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addEditRow}
                    data-testid="btn-add-edit-invoice-row"
                    className="h-8 rounded-md"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Tambah Baris
                  </Button>
                </div>
                <div className="border border-zinc-200 rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-zinc-50">
                      <TableRow>
                        <TableHead>Barang</TableHead>
                        <TableHead className="w-[90px] text-right">Qty MTP</TableHead>
                        <TableHead className="w-[90px] text-right">Qty LTC</TableHead>
                        <TableHead className="w-[160px] text-right">Harga</TableHead>
                        <TableHead className="w-[140px] text-right">Subtotal</TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editing.items.map((it, idx) => {
                        const p = products.find((x) => x.id === it.product_id);
                        const rowQty = (Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0);
                        const rowSubtotal = rowQty * (Number(it.price) || 0);
                        return (
                          <TableRow key={idx}>
                            <TableCell>
                              <ProductPicker
                                testId={`edit-invoice-item-${idx}-product`}
                                products={products}
                                value={it.product_id}
                                onChange={(v) => onPickEditProduct(idx, v)}
                              />
                              {p && (
                                <div className="text-[11px] text-zinc-500 mt-1">
                                  Stok MTP: <span className="font-mono">{p.stock_gudang ?? 0}</span> · Stok LTC:{" "}
                                  <span className="font-mono">{p.stock_toko ?? 0}</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Input
                                data-testid={`edit-invoice-item-${idx}-qty-gudang`}
                                type="number"
                                min="0"
                                className="h-9 text-right"
                                value={it.qty_gudang}
                                onChange={(e) => setEditItem(idx, { qty_gudang: e.target.value })}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                data-testid={`edit-invoice-item-${idx}-qty-toko`}
                                type="number"
                                min="0"
                                className="h-9 text-right"
                                value={it.qty_toko}
                                onChange={(e) => setEditItem(idx, { qty_toko: e.target.value })}
                              />
                            </TableCell>
                            <TableCell>
                              <CurrencyInput
                                data-testid={`edit-invoice-item-${idx}-price`}
                                className="h-9 text-right"
                                value={it.price}
                                onChange={(v) => setEditItem(idx, { price: v })}
                              />
                            </TableCell>
                            <TableCell className="text-right num font-medium">
                              {formatIDR(rowSubtotal)}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => removeEditRow(idx)}
                                className="h-8 w-8 text-rose-600"
                                data-testid={`edit-invoice-item-${idx}-remove`}
                              >
                                <CloseIcon className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Diskon (%)</Label>
                  <Input
                    data-testid="edit-invoice-discount"
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    max="100"
                    value={editing.discount_percent}
                    onChange={(e) => setEditing({ ...editing, discount_percent: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Pajak (%)</Label>
                  <Input
                    data-testid="edit-invoice-tax"
                    type="number"
                    min="0"
                    value={editing.tax_percent}
                    onChange={(e) => setEditing({ ...editing, tax_percent: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Catatan</Label>
                  <Input
                    data-testid="edit-invoice-note"
                    value={editing.note}
                    onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  />
                </div>
              </div>

              {(() => {
                const editSubtotal = editing.items.reduce(
                  (s, it) =>
                    s + ((Number(it.qty_gudang) || 0) + (Number(it.qty_toko) || 0)) * (Number(it.price) || 0),
                  0
                );
                const editDiscAmt = editSubtotal * ((Number(editing.discount_percent) || 0) / 100);
                const editAfterDisc = Math.max(0, editSubtotal - editDiscAmt);
                const editTax = editAfterDisc * ((Number(editing.tax_percent) || 0) / 100);
                const editTotal = editAfterDisc + editTax;
                return (
                  <div className="text-sm space-y-1 border-t border-zinc-200 pt-3">
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Subtotal</span>
                      <span className="num font-medium">{formatIDR(editSubtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Diskon ({Number(editing.discount_percent) || 0}%)</span>
                      <span className="num text-rose-700">- {formatIDR(editDiscAmt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Pajak ({Number(editing.tax_percent) || 0}%)</span>
                      <span className="num">{formatIDR(editTax)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-zinc-200 mt-2">
                      <span className="text-zinc-900 font-semibold">Total</span>
                      <span
                        className="num text-lg font-display font-bold text-zinc-900"
                        data-testid="invoice-edit-total"
                      >
                        {formatIDR(editTotal)}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Batal
                </Button>
                <Button
                  type="submit"
                  data-testid="btn-save-invoice-edit"
                  className="bg-zinc-900 hover:bg-zinc-800"
                >
                  Simpan Perubahan
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* VIEW / PRINT dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto print:shadow-none print:max-w-none" data-testid="invoice-view-dialog">
          {viewing && (
            <div className="space-y-4 print-area">
              <div className="flex items-start justify-between border-b border-zinc-300 pb-4 gap-6">
                <div>
                  <div className="text-sm font-semibold text-zinc-900">
                    {company.name || "Buku Stok"}
                  </div>
                  {company.address && (
                    <div className="text-xs text-zinc-600 max-w-[320px]">{company.address}</div>
                  )}
                  {company.address2 && (
                    <div className="text-xs text-zinc-600 max-w-[320px]">{company.address2}</div>
                  )}
                  {(company.phone || company.email) && (
                    <div className="text-xs text-zinc-500">
                      {[company.phone, company.email].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {company.website && (
                    <div className="text-xs text-zinc-500">{company.website}</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  {company.logo && (
                    <img
                      src={company.logo}
                      alt="Logo"
                      className="max-h-20 max-w-[160px] object-contain"
                      data-testid="invoice-print-logo"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Kepada</div>
                  <div className="text-base font-semibold text-zinc-900">{viewing.customer_name}</div>
                  {viewing.customer_address && (
                    <div className="text-sm text-zinc-600">{viewing.customer_address}</div>
                  )}
                  {viewing.sales_name && (
                    <div className="text-xs text-zinc-500 mt-2">
                      Sales:{" "}
                      <span className="font-medium text-zinc-800">{viewing.sales_name}</span>
                    </div>
                  )}
                  {viewing.po_number && (
                    <div className="text-xs text-zinc-500">
                      No. PO:{" "}
                      <span className="font-medium text-zinc-800 font-mono">{viewing.po_number}</span>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Nomor Faktur</div>
                  <div className="text-2xl font-display font-bold text-zinc-900">{viewing.number}</div>
                  <div className="text-sm text-zinc-600 mt-1">{formatDateID(viewing.date)}</div>
                </div>
              </div>
              <div className="border border-zinc-300 rounded-md overflow-hidden">
                <Table>
                  <TableHeader className="bg-zinc-100">
                    <TableRow>
                      <TableHead className="w-[45px] text-right">#</TableHead>
                      <TableHead>Barang</TableHead>
                      <TableHead className="text-right w-[80px]">Qty</TableHead>
                      <TableHead className="text-right w-[130px]">Harga</TableHead>
                      <TableHead className="text-right w-[130px]">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(viewing.items || []).map((it, i) => {
                      const qg = Number(it.qty_gudang || 0);
                      const qt = it.qty_toko != null ? Number(it.qty_toko) : Number(it.qty || 0) - qg;
                      const isCanceled = !!viewing.canceled;
                      const totalQty = isCanceled ? 0 : qg + qt;
                      const displayName = isCanceled ? "•••••• (dibatalkan)" : it.product_name;
                      const displayCode = isCanceled ? "•••" : it.product_code;
                      const displayPrice = isCanceled ? 0 : it.price;
                      const displaySubtotal = isCanceled ? 0 : it.subtotal;
                      return (
                        <TableRow key={i} className={isCanceled ? "opacity-60" : ""}>
                          <TableCell className="text-right num text-zinc-500">{i + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="font-medium">{displayName}</span>
                              <span className="text-xs text-zinc-500 font-mono">{displayCode}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right num">{isCanceled ? 0 : (totalQty || "—")}</TableCell>
                          <TableCell className="text-right num">{formatIDR(displayPrice)}</TableCell>
                          <TableCell className="text-right num font-medium">{formatIDR(displaySubtotal)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  {viewing.note && (
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Catatan</div>
                      <div className="text-sm text-zinc-700">{viewing.note}</div>
                    </div>
                  )}
                  {(company.bank_name || company.bank_account_number || company.bank_account_holder) && (
                    <div data-testid="invoice-view-bank-info">
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Pembayaran Transfer</div>
                      <div className="text-sm text-zinc-700 leading-relaxed">
                        {company.bank_name && <div className="font-medium text-zinc-900">Bank {company.bank_name}</div>}
                        {company.bank_account_number && (
                          <div>
                            No. Rekening:{" "}
                            <span className="font-mono font-bold text-lg tracking-wider text-zinc-900 ml-1">
                              {company.bank_account_number}
                            </span>
                          </div>
                        )}
                        {company.bank_account_holder && <div>a.n. {company.bank_account_holder}</div>}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-600">Subtotal</span>
                    <span className="num">{formatIDR(viewing.canceled ? 0 : viewing.subtotal)}</span>
                  </div>
                  {!viewing.canceled && viewing.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Diskon</span>
                      <span className="num text-rose-700">- {formatIDR(viewing.discount)}</span>
                    </div>
                  )}
                  {!viewing.canceled && viewing.tax_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Pajak ({viewing.tax_percent}%)</span>
                      <span className="num">{formatIDR(viewing.tax_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-zinc-300 pt-2 mt-2">
                    <span className="font-semibold text-zinc-900">Total</span>
                    <span
                      className={`num text-xl font-display font-bold ${viewing.canceled ? "text-zinc-400" : "text-zinc-900"}`}
                      data-testid="invoice-view-total"
                    >
                      {formatIDR(viewing.canceled ? 0 : viewing.total)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="pt-6 grid grid-cols-2 gap-8 text-xs text-zinc-500">
                <div>
                  <div className="border-t border-zinc-400 pt-2 mt-12 text-center">Penerima</div>
                </div>
                <div>
                  <div className="border-t border-zinc-400 pt-2 mt-12 text-center">Hormat Kami</div>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 no-print">
                <Button variant="outline" onClick={() => setViewing(null)}>Tutup</Button>
                <Button
                  data-testid="btn-save-pdf-invoice"
                  variant="outline"
                  onClick={savePdfInvoice}
                  disabled={savingPdf}
                  className="border-zinc-300"
                >
                  <FileDown className="h-4 w-4 mr-1.5" />
                  {savingPdf ? "Menyimpan…" : "Simpan PDF"}
                </Button>
                <Button data-testid="btn-print-invoice" onClick={printInvoice} className="bg-zinc-900 hover:bg-zinc-800">
                  <Printer className="h-4 w-4 mr-1.5" />
                  Cetak
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

